/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * EosStudyInfoDialog — Edit/view study FTP settings + acknowledgement document templates.
 * Migrated from: lego/page/eos/transfer-media/EosStudyInfoDialog.jsx
 */

import { useState, useEffect, useRef } from 'react'
import { Formik, Field } from 'formik'
import type { FormikProps } from 'formik'
import { Form } from 'react-bootstrap'
import { cloneDeep } from 'lodash'
import { useServiceContext } from '../../../root'
import Modal from '../../../components/Modal'
import Loader from '../../../components/shared/Loader'
import AcknowledgementDocSelection from './acknowledgement-doc-selection/AcknowledgementDocSelection'
import type {
  EosStudyInfoDialogProps,
  FtpContext,
  StudyGroupOption,
  StudyOption,
  EnvironmentOption,
  SelectedDocTypes,
  StudyInfoFormValues
} from './types'

const EosStudyInfoDialog: React.FC<EosStudyInfoDialogProps> = ({ ftpId, preventAutoTransferMedia, isReview }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [isOpen, setIsOpen] = useState(true)
  const [studyData, setStudyData] = useState<Partial<StudyOption>>({})
  const [studyGroup, setStudyGroup] = useState<Partial<StudyGroupOption>>({})
  const [ftpConfig, setFtpConfig] = useState<Partial<FtpContext>>({})
  const [originalRemoteFolder, setOriginalRemoteFolder] = useState('')
  const [isFolderPathError, setIsFolderPathError] = useState(false)
  const [folderPathErrorMessage, setFolderPathErrorMessage] = useState('EOS_NEW_STUDY_REMOTE_FOLDER_NOT_UNIQUE_ERR_MSG')
  const [environmentData, setEnvironmentData] = useState<Partial<EnvironmentOption>>({})
  const [isCaInstance, setIsCaInstance] = useState(false)
  const [ackDocTemplateSelection, setAckDocTemplateSelection] = useState<Partial<SelectedDocTypes>>({})
  const [isLegacyStudy, setIsLegacyStudy] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const formRef = useRef<FormikProps<{ data: StudyInfoFormValues['data'] }> | null>(null)

  const defaultConfig: StudyInfoFormValues = {
    data: {
      password: '',
      port: '',
      studyFolderPath: '',
      transferMode: 'IMPLICIT',
      username: ''
    }
  }

  useEffect(() => {
    Promise.all([context.service.eosService!.loadStudyInfo(ftpId, null), context.api.settings.getFtpContext(ftpId)])
      .then((response: [unknown, unknown]) => {
        const ftpConfigData = response[1] as FtpContext
        const remoteFolder = ftpConfigData?.remoteFolder
        defaultConfig.data.transferMode = ftpConfigData.implicit ? 'IMPLICIT' : 'EXPLICIT'
        setIsLegacyStudy(!!ftpConfigData?.legacyStudy)
        setFtpConfig(ftpConfigData)
        setOriginalRemoteFolder(remoteFolder)
        formRef.current?.setFieldValue('data.port', ftpConfigData?.port, false)
        formRef.current?.setFieldValue('data.studyFolderPath', ftpConfigData?.remoteFolder, false)
        formRef.current?.setFieldValue('data.username', ftpConfigData?.metadata?.user, false)
        formRef.current?.setFieldValue('data.password', ftpConfigData?.metadata?.password, false)
        setIsDataLoaded(true)
        getStudyInfoData(response[0] as { studyGroup: string; study: string; environment: string })
      })
      .catch((error: { data?: { message?: string } }) => {
        context.service.toast.error(error?.data?.message ?? 'ERROR')
        doClose()
      })
  }, [ftpId])

  const getStudyInfoData = (studyInfoResponse: { studyGroup: string; study: string; environment: string }) => {
    context.service.eosService!.getStudyGroups().then((_data) => {
      const data = _data as StudyGroupOption[]
      const studyGroupData = data.find((element) => element.uuid === studyInfoResponse.studyGroup)
      if (!studyGroupData) return
      setStudyGroup(studyGroupData)
      setIsCaInstance(studyGroupData.mccEnabled ?? false)

      context.api.eos.getStudyGroup(studyGroupData.uuid, studyGroupData.mccEnabled ?? false).then((_studiesData) => {
        const studiesData = _studiesData as { elements: StudyOption[] }
        const studyInfoData = studiesData.elements.find((element) => element.uuid === studyInfoResponse.study)
        if (studyInfoData) {
          setStudyData(studyInfoData)
        }
        if (studyGroupData.mccEnabled && studyInfoData) {
          context.api.eos.getPlinthStudyEnvironments(studyInfoData.uuid).then((_envData) => {
            const envData = _envData as EnvironmentOption[]
            const environment = envData.find((element) => element.uuid === studyInfoResponse.environment)
            if (environment) {
              setEnvironmentData(environment)
            }
          })
        }
      })
    })
  }

  const submitDetails = () => {
    if (isReview) {
      doClose()
      return
    }
    setIsSubmitted(true)
    // Trigger validation before checking errors
    formRef.current?.validateForm().then((validationErrors) => {
      if (
        !validationErrors?.data &&
        !isFolderPathError &&
        ackDocTemplateSelection?.siteAckTemplateFsiId &&
        ackDocTemplateSelection?.sponsorAckTemplateFsiId
      ) {
        doSubmit()
      }
    })
  }

  const doSubmit = () => {
    const ftpConfigData = {
      ...ftpConfig,
      implicit: formRef.current?.values.data.transferMode === 'IMPLICIT',
      eosTemplateMapDTO: isLegacyStudy
        ? undefined
        : {
            eosStudyId: ackDocTemplateSelection.eosStudyId ?? 0,
            siteAckTemplateFsiId: ackDocTemplateSelection.siteAckTemplateFsiId,
            siteAckTemplateFsiName: ackDocTemplateSelection.siteAckTemplateFsiName ?? '',
            sponsorAckTemplateFsiId: ackDocTemplateSelection.sponsorAckTemplateFsiId,
            sponsorAckTemplateFsiName: ackDocTemplateSelection.sponsorAckTemplateFsiName ?? ''
          }
    }

    setIsSaving(true)
    context.api.eos
      .updateStudySettings(
        ftpConfigData.id as number,
        ftpConfigData as unknown as Parameters<typeof context.api.eos.updateStudySettings>[1]
      )
      .then(
        () => {
          const studyConfigData = {
            environment: { ...environmentData },
            study: { ...studyData },
            studyGroup: { ...studyGroup }
          }
          window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
          context.$rootScope.$emit('updateFtpIntegrations', ftpConfigData.id ? ftpConfigData.id : 0)
          if (!preventAutoTransferMedia) {
            context.$rootScope.$emit('studyConfigUpdateSuccess', {
              studyConfigData,
              selectedFtpId: ftpId
            })
          }
          context.service.toast.success('EOS_SETUP_STUDY_GROUP_SUCCESS_MSG')
          doClose()
        },
        (error) => {
          context.service.toast.error(error?.body?.message ?? error?.data?.message ?? 'ERROR')
        }
      )
      .finally(() => {
        setIsSaving(false)
      })
  }

  const validatePort = (value: string | number): string | undefined => {
    if (!value) {
      return 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
    } else if (Number(value) > 65535) {
      return 'PORT_MAX_VALUE'
    }
    return undefined
  }

  const checkRequired = (value: string): string | undefined => {
    if (!value) {
      return 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
    }
    return undefined
  }

  const validateFolderPath = (folderPath: string) => {
    if (folderPath?.length) {
      if (folderPath === originalRemoteFolder) {
        return
      }
      if (!ftpConfig.targetFolder) {
        setIsFolderPathError(true)
        return
      }
      context.api.settings
        .validateFolderPath(folderPath, ftpConfig.targetFolder as number, ftpConfig.id as number)
        .then(
          () => {
            setIsFolderPathError(false)
          },
          (error: { data?: { message?: string } }) => {
            setFolderPathErrorMessage((prev) => error?.data?.message ?? prev)
            setIsFolderPathError(true)
          }
        )
    }
  }

  const handleAckDocUpdate = (data: SelectedDocTypes) => {
    setAckDocTemplateSelection(data)
  }

  const doClose = () => {
    setIsOpen(false)
  }

  const titleText = isReview
    ? translate('EOS_SETUP_USERS_WORKFLOWS_STEP1_CONFIRMATION_TITLE') +
      ' - ' +
      translate('EOS_TRANSFER_MEDIA_STUDY_DETAILS_VIEW')
    : translate('EOS_TRANSFER_MEDIA_STUDY_DETAILS_AND_FTP_EDIT')

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={titleText}
      isLevelTwoModal
      className="study-info-dialog level-one-modal"
    >
      <Modal.Body>
        <Loader blocking={isSaving}>
          <Formik
            initialValues={{ data: defaultConfig.data }}
            innerRef={formRef}
            onSubmit={() => {}}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
          >
            {({ values, setFieldValue, errors }) => (
              <>
                <div className="study-name-wrapper flex-container field-wrapper">
                  <label className="control-label half-width">{translate('EMTF_MD_FILTER_TRIAL_NAME')}</label>
                  <div className="half-width slide-left"> {ftpConfig.name} </div>
                </div>
                {!isReview && (
                  <div className="ftp-settings-wrapper">
                    <label className="control-label field-wrapper fs12 ftp-settings-label">
                      {translate('FTP_SETTINGS')}
                    </label>
                    <div className="settings-container">
                      <div className="host-address flex-container field-wrapper">
                        <label className="control-label half-width">{translate('FTP_SETTINGS_HOST_ADDRESS')}</label>
                        <div className="half-width"> {ftpConfig.host} </div>
                      </div>
                      <div className="port-mode-wrapper flex-container field-wrapper">
                        <div className="port flex-container half-width">
                          <label className="control-label">{translate('FTP_FORM_LABEL_PORT')}</label>
                        </div>
                        <div className="transfer-mode-wrapper flex-container half-width">
                          <div className="flex-container">
                            <div className="port-input">
                              <Field
                                className="form-control field-control"
                                type="number"
                                name="data.port"
                                value={ftpConfig.port}
                                validate={validatePort}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const ftpData = cloneDeep(ftpConfig)
                                  ftpData.port =
                                    e.target.value.length > 5 ? (ftpConfig.port as number) : Number(e.target.value)
                                  setFtpConfig(ftpData)
                                  setFieldValue('data.port', e.target.value)
                                }}
                                disabled={isReview}
                              />
                            </div>
                            <div className="transfer-mode flex-container">
                              <label className="control-label">{translate('FTPS Transfer Mode')}</label>
                              <div className="selection-wrapper">
                                <Form.Check
                                  type="radio"
                                  name="data.transferMode"
                                  className="classic radio-selection w100p"
                                  label={translate('IMPLICIT')}
                                  checked={values.data.transferMode === 'IMPLICIT'}
                                  onClick={() => setFieldValue('data.transferMode', 'IMPLICIT')}
                                  onChange={() => {}}
                                  disabled={isReview}
                                />
                                <Form.Check
                                  type="radio"
                                  name="data.transferMode"
                                  className="classic radio-selection w100p"
                                  label={translate('EXPLICIT')}
                                  checked={values.data.transferMode === 'EXPLICIT'}
                                  onClick={() => setFieldValue('data.transferMode', 'EXPLICIT')}
                                  onChange={() => {}}
                                  disabled={isReview}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="error port">
                        {isSubmitted && (errors as Record<string, Record<string, string>>)?.data?.port && (
                          <span className="error-message-wrapper">
                            {translate((errors as Record<string, Record<string, string>>).data.port)}
                          </span>
                        )}
                      </div>
                      <div className="study-path-wrapper flex-container field-wrapper">
                        <label className="control-label half-width">
                          {translate('EOS_NEW_STUDY_FOLDER_PATH_LABEL')}
                        </label>
                        <Field
                          className="form-control field-control half-width"
                          type="text"
                          name="data.studyFolderPath"
                          value={ftpConfig.remoteFolder}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setIsFolderPathError(false)
                            let hasLeadingChar = true
                            if (e.target.value.length && e.target.value[0] !== '/') {
                              hasLeadingChar = false
                            }
                            validateFolderPath(e.target.value)
                            const ftpdata = cloneDeep(ftpConfig)
                            ftpdata.remoteFolder = hasLeadingChar ? e.target.value : '/' + e.target.value
                            setFtpConfig(ftpdata)
                            setFieldValue(
                              'data.studyFolderPath',
                              hasLeadingChar ? e.target.value : '/' + e.target.value
                            )
                          }}
                          validate={checkRequired}
                          disabled={isReview}
                          maxLength={4096}
                        />
                      </div>
                      <div className="error study-folder">
                        {isSubmitted && (errors as Record<string, Record<string, string>>)?.data?.studyFolderPath && (
                          <span className="error-message-wrapper">
                            {translate((errors as Record<string, Record<string, string>>).data.studyFolderPath)}
                          </span>
                        )}
                        {isFolderPathError && (
                          <span className="error-message-wrapper">{translate(folderPathErrorMessage)}</span>
                        )}
                      </div>
                      {!isReview && (
                        <div className="username-wrapper flex-container field-wrapper">
                          <label className="control-label half-width">{translate('FTP_FORM_LABEL_USERNAME')}</label>
                          <Field
                            className="form-control field-control half-width"
                            type="text"
                            name="data.username"
                            value={ftpConfig?.metadata?.user}
                            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                              const ftpdata = cloneDeep(ftpConfig)
                              if (ftpdata.metadata) {
                                ftpdata.metadata.user = evt.target.value
                              }
                              setFtpConfig(ftpdata)
                              setFieldValue('data.username', evt.target.value)
                            }}
                            validate={checkRequired}
                          />
                        </div>
                      )}
                      <div className="error username">
                        {isSubmitted && (errors as Record<string, Record<string, string>>)?.data?.username && (
                          <span className="error-message-wrapper">
                            {translate((errors as Record<string, Record<string, string>>).data.username)}
                          </span>
                        )}
                      </div>
                      {!isReview && (
                        <div className="password-wrapper flex-container field-wrapper">
                          <label className="control-label half-width">{translate('FTP_FORM_LABEL_PASSWORD')}</label>
                          <Field
                            className="form-control field-control half-width"
                            type="password"
                            name="data.password"
                            placeholder="*******"
                            value={ftpConfig?.metadata?.password}
                            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                              const ftpdata = cloneDeep(ftpConfig)
                              if (ftpdata.metadata) {
                                ftpdata.metadata.password = evt.target.value
                              }
                              setFtpConfig(ftpdata)
                              setFieldValue('data.password', evt.target.value)
                            }}
                          />
                        </div>
                      )}
                      <div className="destination-folder-wrapper flex-container field-wrapper">
                        <label className="control-label half-width">
                          {translate('FTP_FORM_LABEL_RCM_DESTINATION_FOLDER')}
                        </label>
                        <div className="half-width"> {ftpConfig?.targetFolderName} </div>
                      </div>
                    </div>
                  </div>
                )}
                {isReview && (
                  <div className="destination-folder-wrapper flex-container field-wrapper">
                    <label className="control-label half-width">
                      {translate('FTP_FORM_LABEL_RCM_DESTINATION_FOLDER')}
                    </label>
                    <div className="half-width slide-left"> {ftpConfig?.targetFolderName} </div>
                  </div>
                )}
                <div className="study-info-select-wrapper">
                  <div className="study-group-wrapper flex-container field-wrapper">
                    <label className="control-label half-width">{translate('EOS_SETUP_STUDY_GROUP')}</label>
                    <div className="half-width slide-left"> {studyGroup?.name} </div>
                  </div>
                  <div className="study-wrapper flex-container field-wrapper">
                    <label className="control-label half-width">{translate('EOS_SETUP_STUDY')}</label>
                    <div className="half-width slide-left"> {studyData?.name} </div>
                  </div>
                  {isCaInstance && (
                    <div className="environment-wrapper flex-container field-wrapper">
                      <label className="control-label half-width">{translate('EMTF_MD_ENVIRONMENT')}</label>
                      <div className="half-width slide-left"> {environmentData?.name} </div>
                    </div>
                  )}
                </div>
                <div className="flex-container fs12 parent-container">
                  <AcknowledgementDocSelection
                    handleAckDocUpdate={handleAckDocUpdate}
                    contextId={ftpConfig?.id ?? undefined}
                    isLegacyStudy={isLegacyStudy}
                    isReview={isReview}
                  />
                </div>
              </>
            )}
          </Formik>
        </Loader>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Button variant="link" onClick={doClose}>
          {translate('CANCEL')}
        </Modal.Footer.Button>
        <Modal.Footer.Button variant="primary" onClick={submitDetails} disabled={isSaving}>
          {translate(isReview ? 'OK' : 'SAVE')}
        </Modal.Footer.Button>
      </Modal.Footer>
    </Modal>
  )
}

export default EosStudyInfoDialog
